import { FunctionComponent } from 'preact';
import { useMemo, useEffect, useLayoutEffect } from 'preact/hooks';

import { Backdrop } from '../../components/Backdrop/Backdrop';
import { Typography } from '../../components/Typography/Typography';
import { Button } from '../../components/Button/Button';
import { useTranslation } from '../../../../utils/useTranslation';
import { AssetTitle } from '../../components/AssetTitle/AssetTitle';
import ns from '../../translations';
import { useNextAsset } from '../../hooks/NextAssetProvider/NextAssetProvider';
import { usePlayerContainer } from '../../hooks/usePlayerContainer';
import { getImageSrc } from '../../utils/image';
import { PaywallButtons } from '../../components/PaywallButtons/PaywallButtons';
import { usePaywallState } from '../../hooks/usePaywallState';
import { getSubscriptionLabel } from '../../../../utils/subscription';
import { useViewportType } from '../../hooks/useViewportType';
import { useProvider } from '../../hooks/useProvider';
import {
  getAssetAccess,
  getAssetImage,
  isSubscriptionAccessLevel,
} from '../../../../utils/player/asset';
import { IconButton } from '../../components/IconButton/IconButton';

import { ReplayButton } from './ReplayButton';
import { useAccessDefinitions } from '../../hooks/AccessDefinitionsProvider';
import { useTpaasTracker } from '../../hooks/useTpaasTracker';
import { useIcons } from '../../hooks/useIcons';

export interface UpNextProps {
  onCancelClick: (e: MouseEvent) => void;
  handleReplay: () => void;
  onSkipClick: (e: MouseEvent) => void;
  remainingTime: number;
  countdownDuration: number;
  disablePreview?: boolean;
}

const getProgressPercentage = (
  remainingTime: number,
  countdownDuration: number
) => {
  return ((countdownDuration - remainingTime) / countdownDuration) * 100;
};

export const UpNextLayout: FunctionComponent<UpNextProps> = ({
  onCancelClick,
  handleReplay,
  onSkipClick,
  countdownDuration,
  remainingTime,
  disablePreview,
}) => {
  const playerContainer = usePlayerContainer();
  const { configProvider } = useProvider();
  const { nextAsset } = useNextAsset();
  const { isMobile } = useViewportType();
  const { t } = useTranslation(ns);
  const { accessDefinitions } = useAccessDefinitions();
  const accessLevel = getAssetAccess(nextAsset);
  const { trackImpressionUIElement } = useTpaasTracker();
  const { Play, Cancel } = useIcons();

  const { hasAccess } = usePaywallState(accessLevel);

  useEffect(() => {
    trackImpressionUIElement({
      elementType: 'div',
      name: 'PlayNextCountdown',
    });
  }, [trackImpressionUIElement]);

  const element = useMemo(
    () => playerContainer.querySelector<HTMLDivElement>('.jw-media'),
    [playerContainer]
  );

  const posterSrc = useMemo(
    () =>
      getImageSrc(
        getAssetImage(nextAsset!, 'main'),
        playerContainer.clientWidth
      ),
    [playerContainer, nextAsset]
  );

  useLayoutEffect(() => {
    if (!element || disablePreview) {
      return;
    }

    element.classList.add('vgp-media-next-up');

    if (isMobile) {
      element.classList.add('vgp-mobile');
    }

    return () => {
      element.classList.remove('vgp-media-next-up');

      if (isMobile) {
        element.classList.remove('vgp-mobile');
      }
    };
  }, [element, disablePreview, isMobile]);

  useEffect(() => {
    if (!element) {
      return;
    }

    element.addEventListener('click', onCancelClick);

    return () => element.removeEventListener('click', onCancelClick);
  }, [element, onCancelClick]);

  const displaySubscriptionLabel =
    isSubscriptionAccessLevel(configProvider, accessLevel) && !hasAccess;

  return (
    <div
      className="vgp-up-next-layout"
      style={{ backgroundImage: `url(${posterSrc})` }}
    >
      <IconButton
        onClick={onCancelClick}
        color="secondary"
        filled
        className="vgp-cancel-button"
      >
        <Cancel />
      </IconButton>

      <Backdrop isVisible>
        <div className="vgp-up-next-content">
          {displaySubscriptionLabel && (
            <Typography variant="label" size="large" color="white">
              {t('Watch now with subscription', {
                subscription: getSubscriptionLabel(
                  accessDefinitions,
                  accessLevel
                ),
              })}
            </Typography>
          )}
          <div className="vgp-up-next-title">
            <AssetTitle asset={nextAsset!} />
          </div>
          <div className="vgp-up-next-buttons">
            <div className="vgp-up-replay-button">
              <ReplayButton handleReplay={handleReplay} />
            </div>
            {hasAccess ? (
              <Button
                size={isMobile ? 'medium' : 'large'}
                color="primary"
                onClick={onSkipClick}
                className="vgp-up-next-skip"
              >
                <>
                  <Play />
                  <div
                    className="vgp-up-next-progress"
                    style={{
                      width: `${getProgressPercentage(
                        remainingTime,
                        countdownDuration
                      )}%`,
                    }}
                  />
                </>
                <Typography
                  variant="label"
                  size={isMobile ? 'medium' : 'large'}
                  color="white"
                >
                  {t('Play')}
                </Typography>
              </Button>
            ) : (
              <PaywallButtons
                asset={nextAsset!}
                accessLevel={accessLevel}
                size={isMobile ? 'medium' : 'large'}
                color="secondary"
              />
            )}
          </div>
        </div>
      </Backdrop>
    </div>
  );
};
